import { useState } from "react";
import { ApiError, errorText, post, refresh } from "../lib/api.ts";
import type { Prompt, Question } from "../lib/types.ts";
import { useToast } from "./ui.tsx";

/** What a permission request is asking for, in a sentence and a code block. */
export function describePermission(toolName: string, input: unknown): { action: string; body: string; note?: string } {
  const i = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const s = (v: unknown) => (typeof v === "string" ? v : "");
  switch (toolName) {
    case "Bash":
      return { action: "Run a command", body: s(i.command), note: s(i.description) || undefined };
    case "Edit":
    case "MultiEdit":
      return { action: "Edit a file", body: s(i.file_path) };
    case "Write":
      return { action: "Write a file", body: `${s(i.file_path)}  (${s(i.content).split("\n").length} lines)` };
    case "NotebookEdit":
      return { action: "Edit a notebook", body: s(i.notebook_path) };
    case "WebFetch":
      return { action: "Fetch a URL", body: s(i.url), note: s(i.prompt) || undefined };
    case "WebSearch":
      return { action: "Search the web", body: s(i.query) };
    default:
      return { action: `Use ${toolName}`, body: JSON.stringify(input ?? {}, null, 2) };
  }
}

async function answer(promptId: string, body: unknown): Promise<void> {
  await post(`/api/prompts/${promptId}/answer`, { answer: body, via: "web", by: "web" });
  refresh("/api/prompts", "/api/overview", "/api/sessions");
}

export function PromptCard({ prompt }: { prompt: Pick<Prompt, "id" | "kind" | "toolName" | "input" | "options"> }) {
  return prompt.kind === "question" ? <QuestionForm prompt={prompt} /> : <PermissionForm prompt={prompt} />;
}

function useSubmit() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const submit = async (promptId: string, body: unknown, label: string) => {
    setBusy(true);
    try {
      await answer(promptId, body);
      setDone(label);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const d = err.details as { resolvedVia?: string } | undefined;
        setDone(`Already answered${d?.resolvedVia ? ` via ${d.resolvedVia}` : ""}`);
      } else toast(errorText(err), "bad");
    } finally {
      setBusy(false);
    }
  };
  return { busy, done, submit };
}

function QuestionForm({ prompt }: { prompt: Pick<Prompt, "id" | "options"> }) {
  const questions: Question[] = prompt.options ?? [];
  const [picked, setPicked] = useState<Record<number, string[]>>({});
  const [other, setOther] = useState<Record<number, string | null>>({});
  const { busy, done, submit } = useSubmit();

  if (done) return <div className="callout ok">✓ {done}</div>;

  const answerFor = (qi: number) => {
    const labels = [...(picked[qi] ?? [])];
    const o = other[qi]?.trim();
    if (o) labels.push(o);
    return labels.join(", ");
  };
  const complete = questions.every((_, qi) => answerFor(qi));
  const send = (override?: Record<number, string>) => {
    const answers = Object.fromEntries(questions.map((q, qi) => [q.question, override?.[qi] ?? answerFor(qi)]));
    void submit(prompt.id, { kind: "question", answers }, `Answered: ${Object.values(answers).join("; ")}`);
  };

  return (
    <div className="stack">
      {questions.map((q, qi) => (
        <div key={qi} className="stack tight">
          {q.header && <span className="pill warn" style={{ justifySelf: "start" }}>{q.header}</span>}
          <div className="q">{q.question}</div>
          <div className="opts">
            {q.options.map((o) => {
              const on = picked[qi]?.includes(o.label);
              return (
                <button
                  key={o.label}
                  type="button"
                  className={`opt ${on ? "on" : ""}`}
                  disabled={busy}
                  onClick={() => {
                    // One single-choice question: a click is the answer.
                    if (questions.length === 1 && !q.multiSelect) return send({ 0: o.label });
                    setPicked((p) => {
                      const cur = p[qi] ?? [];
                      const next = q.multiSelect ? (on ? cur.filter((x) => x !== o.label) : [...cur, o.label]) : [o.label];
                      return { ...p, [qi]: next };
                    });
                    if (!q.multiSelect) setOther((x) => ({ ...x, [qi]: null }));
                  }}
                >
                  <span>
                    {q.multiSelect ? (on ? "☑ " : "☐ ") : ""}
                    {o.label}
                  </span>
                  {o.description && <small>{o.description}</small>}
                </button>
              );
            })}
            <button
              type="button"
              className={`opt ${other[qi] !== undefined && other[qi] !== null ? "on" : ""}`}
              onClick={() => {
                setOther((x) => ({ ...x, [qi]: x[qi] == null ? "" : null }));
                if (!q.multiSelect) setPicked((p) => ({ ...p, [qi]: [] }));
              }}
            >
              Other…
            </button>
          </div>
          {other[qi] != null && (
            <textarea
              autoFocus
              placeholder="Your answer"
              value={other[qi] ?? ""}
              onChange={(e) => setOther((x) => ({ ...x, [qi]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && complete) send();
              }}
            />
          )}
        </div>
      ))}
      {(questions.length > 1 || questions.some((q, qi) => q.multiSelect || other[qi] != null)) && (
        <div className="row">
          <button className="btn primary" disabled={!complete || busy} onClick={() => send()}>
            {busy ? "Sending…" : "Send answer"}
          </button>
        </div>
      )}
    </div>
  );
}

function PermissionForm({ prompt }: { prompt: Pick<Prompt, "id" | "toolName" | "input"> }) {
  const { action, body, note } = describePermission(prompt.toolName, prompt.input);
  const [denying, setDenying] = useState<string | null>(null);
  const { busy, done, submit } = useSubmit();
  if (done) return <div className="callout ok">✓ {done}</div>;
  const decide = (decision: "allow" | "deny" | "always", message?: string) =>
    submit(prompt.id, { kind: "permission", decision, message }, { allow: "Approved", deny: "Denied", always: "Always allowed" }[decision]);

  return (
    <div className="stack">
      <div className="q">{action}</div>
      <pre>
        <code>{body}</code>
      </pre>
      {note && <div className="muted small">Claude says: {note}</div>}
      {denying !== null && (
        <textarea autoFocus placeholder="Optional: tell Claude why, or what to do instead" value={denying} onChange={(e) => setDenying(e.target.value)} />
      )}
      <div className="row">
        {denying === null ? (
          <>
            <button className="btn ok" disabled={busy} onClick={() => decide("allow")}>
              Approve
            </button>
            <button className="btn danger" disabled={busy} onClick={() => decide("deny")}>
              Deny
            </button>
            <button className="btn" disabled={busy} onClick={() => decide("always")} title="Saves a rule to the worktree's .claude/settings.local.json">
              Always allow
            </button>
            <button className="btn ghost sm" onClick={() => setDenying("")}>
              Deny with a message…
            </button>
          </>
        ) : (
          <>
            <button className="btn danger" disabled={busy} onClick={() => decide("deny", denying || undefined)}>
              Deny
            </button>
            <button className="btn ghost" onClick={() => setDenying(null)}>
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
